import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowSquareOut,
  CaretLeft,
  CheckCircle,
  CornersOut,
  Eye,
  EyeSlash,
  FilePdf,
  Flask,
  Paperclip,
  Plus,
  PuzzlePiece,
  UploadSimple,
  X,
} from '@phosphor-icons/react';
import { EVIDENCE_TAGS, type SubmissionAttachment } from '@cvs/shared';
import { api } from '../lib/api';
import { useCourse } from '../lib/course';
import { useAsync } from '../lib/useAsync';
import { formatRelative } from '../lib/format';
import { RichText } from '../components/RichText';
import { Button, Card, ErrorState, LoadingState, Pill, Spinner, SubmissionPill, cx } from '../components/ui';

export function LabDetail() {
  const { id } = useParams();
  const { coursePath } = useCourse();
  const s = useAsync(() => api.labs(), []);

  if (s.loading) return <LoadingState />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;

  const lab = s.data.labs.find((l) => l.id === id);
  if (!lab) return <ErrorState message="This project or lab could not be found." />;
  const submission = s.data.submissions.find((sub) => sub.labId === lab.id) ?? null;
  const isExercise = lab.kind === 'exercise';
  const kindLabel = lab.kind === 'project' ? 'Section project' : isExercise ? 'Exercise' : 'Hands-on lab';
  const backLabel = lab.kind === 'project' ? 'Projects' : isExercise ? 'Exercises' : 'Labs';

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link
        to={coursePath('labs')}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition hover:text-brand-teal"
      >
        <CaretLeft size={16} weight="bold" /> Back to {backLabel}
      </Link>

      <header className="flex items-start gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-brand-teal">
          {isExercise ? <PuzzlePiece size={26} weight="bold" /> : <Flask size={26} weight="bold" />}
        </span>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold leading-tight text-brand-navy">{lab.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Pill tone="teal">{kindLabel}</Pill>
            {lab.env && <Pill tone="violet">{lab.env}</Pill>}
            <span className="text-sm font-semibold text-slate-500">{lab.points} pts</span>
            {submission && <SubmissionPill status={submission.status} />}
          </div>
        </div>
      </header>

      {lab.guideHtml && <HtmlGuideFrame html={lab.guideHtml} title={`${lab.title} — guide`} />}

      {lab.resourceUrl && <GuideViewer lab={lab} />}

      {lab.answerHtml && <AnswerReveal html={lab.answerHtml} title={`${lab.title} — answer`} />}

      {lab.instructions.trim() && (
        <Card className="p-6 sm:p-8">
          <RichText text={lab.instructions} />
        </Card>
      )}

      {isExercise ? (
        <p className="text-center text-sm text-slate-400">
          Self-check exercise{lab.answerHtml ? ' — reveal the answer above to check your work' : ''}. No submission needed.
        </p>
      ) : (
        <SubmissionPanel lab={lab} submission={submission} onChanged={s.reload} />
      )}
    </div>
  );
}

const GUIDE_LOADING = (
  <div className="flex h-[78vh] min-h-[440px] items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-slate-900 text-sm text-slate-300">
    <Spinner size={26} className="text-white" /> Loading the guide…
  </div>
);

/**
 * The lab's guide, rendered inline. The API returns a PDF as a URL the browser
 * fetches directly — a SharePoint guide is staged into our storage and served as
 * a SAS URL (CORS-allowed), so pdf.js renders it in the same viewer the class
 * slides use. Anything else falls back to an "open in a new tab" button.
 */
function GuideViewer({ lab }: { lab: { id: string; title: string; resourceUrl?: string | null } }) {
  const [guide, setGuide] = useState<{
    url?: string | null;
    html?: string | null;
    kind: 'pdf' | 'html' | 'link' | 'none';
  } | null>(null);

  useEffect(() => {
    let active = true;
    setGuide(null);
    api
      .labGuide(lab.id)
      .then((g) => active && setGuide(g))
      .catch(() => active && setGuide({ url: lab.resourceUrl ?? null, kind: 'link' }));
    return () => {
      active = false;
    };
  }, [lab.id, lab.resourceUrl]);

  if (guide === null) return GUIDE_LOADING;

  if (guide.kind === 'pdf' && guide.url) {
    return <PdfGuideFrame url={guide.url} title={`${lab.title} — guide`} />;
  }

  if (guide.kind === 'html' && guide.html) {
    return <HtmlGuideFrame html={guide.html} title={`${lab.title} — guide`} />;
  }

  return (
    <a
      href={lab.resourceUrl ?? '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 rounded-xl border border-brand-teal/30 bg-teal-50/60 p-4 transition hover:border-brand-teal hover:bg-teal-50"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-brand-teal shadow-sm">
        <FilePdf size={22} weight="fill" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-bold text-brand-navy">Open the full guide</span>
        <span className="block text-sm text-slate-500">Opens in a new tab</span>
      </span>
      <ArrowSquareOut size={20} weight="bold" className="shrink-0 text-brand-teal" />
    </a>
  );
}

/** Fullscreen toggle bound to a container ref. */
function useFullscreen() {
  const ref = useRef<HTMLDivElement>(null);
  const [isFull, setIsFull] = useState(false);
  useEffect(() => {
    const onFs = () => setIsFull(document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);
  const toggle = () => (isFull ? void document.exitFullscreen() : void ref.current?.requestFullscreen?.());
  return { ref, isFull, toggle };
}

const FS_BTN =
  'inline-flex items-center gap-1.5 rounded-lg bg-brand-navy/80 px-3 py-1.5 text-xs font-semibold text-white shadow-sm backdrop-blur transition hover:bg-brand-navy';

/**
 * Put a Copy button on every <pre> inside the guide frame.
 *
 * The frame is sandboxed without allow-scripts, so the guide cannot do this for
 * itself — but it IS allow-same-origin, so we can. The click handler runs in
 * this document's realm, which is not sandboxed.
 *
 * `document.execCommand` is deprecated and used deliberately: user activation
 * does not cross the sandbox boundary, so navigator.clipboard is refused for a
 * click that happened inside the frame. It is tried first anyway, for the day
 * that changes.
 */
function addCopyButtons(doc: Document) {
  doc.querySelectorAll<HTMLPreElement>('pre').forEach((pre) => {
    if (pre.dataset.copyReady) return; // idempotent — the observer re-runs this
    pre.dataset.copyReady = '1';

    // Captured BEFORE the button is appended, so the word "Copy" is never in it.
    const text = pre.textContent ?? '';
    if (!text.trim()) return;

    if (!pre.style.position) pre.style.position = 'relative';

    const btn = doc.createElement('button');
    btn.type = 'button';
    btn.textContent = 'Copy';
    btn.setAttribute('aria-label', 'Copy this command');
    const base =
      'position:absolute;top:8px;right:8px;z-index:2;cursor:pointer;' +
      "font-family:'IBM Plex Mono',ui-monospace,monospace;font-size:10px;letter-spacing:.12em;" +
      'text-transform:uppercase;padding:4px 10px;border-radius:20px;' +
      'border:1px solid rgba(45,212,191,.4);background:rgba(45,212,191,.1);color:#2dd4bf;';
    btn.setAttribute('style', base);

    const flash = (label: string) => {
      btn.textContent = label;
      window.setTimeout(() => {
        btn.textContent = 'Copy';
      }, 1400);
    };

    const legacyCopy = () => {
      // The textarea lives in THIS document, where scripts are not sandboxed.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:-1000px;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      let ok = false;
      try {
        ok = document.execCommand('copy');
      } catch {
        ok = false;
      }
      document.body.removeChild(ta);
      flash(ok ? 'Copied' : 'Press Ctrl+C');
      if (!ok) {
        // Last resort: leave it selected so Ctrl+C works.
        const r = doc.createRange();
        r.selectNodeContents(pre);
        const sel = doc.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(r);
      }
    };

    btn.addEventListener('click', () => {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(() => flash('Copied'), legacyCopy);
      } else {
        legacyCopy();
      }
    });

    pre.appendChild(btn);
  });
}

/**
 * Instructor HTML guide, auto-sized to its content so the page scrolls naturally
 * (no nested scrollbar), with a fullscreen toggle. Sandboxed with
 * allow-same-origin so the parent can measure the content height, but NOT
 * allow-scripts — the guide's own scripts never run.
 *
 * Same-origin is also what lets us add a Copy button to each command block from
 * out here. The guide cannot do it itself, and nothing about the sandbox is
 * relaxed to allow it.
 */
function HtmlGuideFrame({ html, title }: { html: string; title: string }) {
  const { ref, isFull, toggle } = useFullscreen();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(560);

  useEffect(() => {
    const iframe = frameRef.current;
    if (!iframe) return;
    let ro: ResizeObserver | null = null;
    const measure = () => {
      const doc = iframe.contentDocument;
      if (doc) setHeight(Math.max(440, doc.documentElement.scrollHeight));
    };
    const onLoad = () => {
      const doc0 = iframe.contentDocument;
      if (doc0) addCopyButtons(doc0);
      measure();
      const doc = iframe.contentDocument;
      if (doc && 'ResizeObserver' in window) {
        ro = new ResizeObserver(measure);
        ro.observe(doc.documentElement);
      }
    };
    iframe.addEventListener('load', onLoad);
    if (iframe.contentDocument?.readyState === 'complete') onLoad();
    return () => {
      iframe.removeEventListener('load', onLoad);
      ro?.disconnect();
    };
  }, [html]);

  return (
    <div
      ref={ref}
      className={cx(
        'relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card',
        isFull && 'flex flex-col rounded-none',
      )}
    >
      <button type="button" onClick={toggle} className={cx(FS_BTN, 'absolute right-3 top-3 z-10')}>
        <CornersOut size={15} weight="bold" /> {isFull ? 'Exit' : 'Fullscreen'}
      </button>
      <iframe
        ref={frameRef}
        title={title}
        srcDoc={html}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        style={{ height: isFull ? '100%' : height }}
        className={cx('w-full border-0 bg-white', isFull && 'min-h-0 flex-1')}
      />
    </div>
  );
}

/**
 * Guide PDF rendered as a continuous, scrollable document (the browser's native
 * PDF viewer) rather than a page-by-page slide deck — with fullscreen + open-in-tab.
 */
function PdfGuideFrame({ url, title }: { url: string; title: string }) {
  const { ref, isFull, toggle } = useFullscreen();
  return (
    <div
      ref={ref}
      className={cx('relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100', isFull && 'rounded-none')}
    >
      <div className="absolute right-3 top-3 z-10 flex gap-2">
        <button type="button" onClick={toggle} className={FS_BTN}>
          <CornersOut size={15} weight="bold" /> {isFull ? 'Exit' : 'Fullscreen'}
        </button>
        <a href={url} target="_blank" rel="noopener noreferrer" className={FS_BTN}>
          <ArrowSquareOut size={15} weight="bold" /> Open
        </a>
      </div>
      <iframe
        title={title}
        src={`${url}#view=FitH`}
        className={cx('w-full border-0', isFull ? 'h-screen' : 'h-[82vh] min-h-[440px]')}
      />
    </div>
  );
}

/** An exercise's model answer, hidden behind a reveal toggle so students try first. */
function AnswerReveal({ html, title }: { html: string; title: string }) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="inline-flex items-center gap-2 rounded-xl border border-brand-teal/40 bg-teal-50/70 px-4 py-2.5 text-sm font-bold text-brand-navy transition hover:border-brand-teal hover:bg-teal-50"
      >
        {show ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
        {show ? 'Hide answer' : 'Reveal answer'}
      </button>
      {show && <HtmlGuideFrame html={html} title={title} />}
    </div>
  );
}

function SubmissionPanel({
  lab,
  submission,
  onChanged,
}: {
  lab: { id: string; title: string; points: number };
  submission: { status: string; grade?: number | null; feedback?: string | null; submittedAt: string } | null;
  onChanged: () => void;
}) {
  const [rows, setRows] = useState<{ file: File; tag: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(!submission);
  const inputRef = useRef<HTMLInputElement>(null);
  const graded = submission?.status === 'graded';

  function addFiles(list: FileList | null) {
    if (!list?.length) return;
    const picked = Array.from(list).map((file) => ({ file, tag: '' }));
    setRows((cur) => [...cur, ...picked].slice(0, 10)); // cap at 10 files
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  async function handleSubmit() {
    if (rows.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const attachments: SubmissionAttachment[] = [];
      for (const { file, tag } of rows) {
        const { uploadUrl, fileUrl } = await api.uploadSas(file.name, file.type);
        if (uploadUrl) {
          const res = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type },
            body: file,
          });
          if (!res.ok) throw new Error(`Upload failed for “${file.name}” — please try again.`);
        }
        attachments.push({ url: fileUrl, name: file.name, tag: tag.trim() || null });
      }
      await api.submitLab(lab.id, attachments);
      setRows([]);
      if (inputRef.current) inputRef.current.value = '';
      setShowForm(false);
      onChanged();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Submission failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="space-y-4 p-6">
      <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Your submission</h2>

      {submission && (
        <div className="rounded-xl bg-slate-50 p-4">
          {graded ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle size={20} weight="fill" className="text-emerald-600" />
                <span className="text-sm font-bold text-brand-navy">
                  {submission.grade ?? 0}/{lab.points}
                </span>
                <span className="label-mono">Graded</span>
              </div>
              {submission.feedback && (
                <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-900">
                  {submission.feedback}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-600">Awaiting review · submitted {formatRelative(submission.submittedAt)}</p>
          )}
        </div>
      )}

      {submission && !showForm ? (
        <Button variant="secondary" icon={UploadSimple} onClick={() => setShowForm(true)}>
          Resubmit evidence
        </Button>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Work through the steps above, then upload your evidence. You can attach several files and label each one
            (e.g. a screenshot, a config export, a query).
          </p>

          {rows.length > 0 && (
            <ul className="space-y-2">
              {rows.map((row, i) => (
                <li
                  key={i}
                  className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2.5"
                >
                  <Paperclip size={16} weight="bold" className="shrink-0 text-slate-400" />
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-700" title={row.file.name}>
                    {row.file.name}
                  </span>
                  <input
                    list="cvs-evidence-tags"
                    value={row.tag}
                    onChange={(e) =>
                      setRows((cur) => cur.map((r, j) => (j === i ? { ...r, tag: e.target.value } : r)))
                    }
                    placeholder="Tag (optional)"
                    disabled={busy}
                    className="input h-9 w-44 text-sm"
                  />
                  <button
                    type="button"
                    aria-label={`Remove ${row.file.name}`}
                    onClick={() => setRows((cur) => cur.filter((_, j) => j !== i))}
                    disabled={busy}
                    className="shrink-0 rounded-lg p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                  >
                    <X size={16} weight="bold" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <datalist id="cvs-evidence-tags">
            {EVIDENCE_TAGS.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>

          <div className="flex flex-wrap items-center gap-3">
            <label className={cx('btn-secondary cursor-pointer', busy && 'pointer-events-none opacity-60')}>
              <Plus size={16} weight="bold" /> {rows.length ? 'Add more files' : 'Choose files'}
              <span className="sr-only">Evidence files for {lab.title}</span>
              <input
                ref={inputRef}
                type="file"
                multiple
                className="hidden"
                disabled={busy}
                onChange={(e) => addFiles(e.target.files)}
              />
            </label>
            <Button icon={UploadSimple} onClick={handleSubmit} loading={busy} disabled={rows.length === 0 || busy}>
              Submit {rows.length > 1 ? `${rows.length} files` : 'evidence'}
            </Button>
            {submission && showForm && !busy && (
              <Button
                variant="ghost"
                onClick={() => {
                  setShowForm(false);
                  setRows([]);
                }}
              >
                Cancel
              </Button>
            )}
          </div>
          {error && <p className="text-sm font-medium text-rose-600">{error}</p>}
        </div>
      )}
    </Card>
  );
}
